// Strategic view of the land (Civ's "=" toggle): flat pastel terrain classes, faint ink hex outlines,
// an ink coast and simple ink icons, instead of the lit 3D ground.
//
//   terrain class ─► pastel colour ─┐
//   hills, woods, marsh, peaks, cities ─► ink icon (per hex, from signed distances)
//   hex outline (15 % ink) + coast line ─┘
//   occupation and annexation ─► diagonal stripes in the controller's colour
//   claimed by the leader's view ─► the other diagonal, in the owner's colour
//
// One uniform, `amount`, runs 0 → 1 over STRATEGIC_FADE_S, so the ground cross-fades from the 3D
// look. The shader is compiled in once and gated by that uniform. The layer sits before lakes (100)
// and rivers (110), which stay on top, and before the lens (250) and the borders (300), so a lens
// and crisp borders work in this view as in 3D. The sea is the sea veil's job (seaVeil.ts) and the
// props are dissolved by featureFog.ts.

import { If, float, fwidth, ivec2, mix, select, smoothstep, textureLoad, uniform, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { FEATURE, HILL, TERRAIN } from '../../core/schema.ts';
import type { ViewMode } from '../api.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import { rgb, screen, screenNode } from '../tsl/color.ts';
import { INRADIUS } from '../tsl/hex.ts';
import { CLAIM_ROW, type OverlayData } from './overlayData.ts';
import { INK } from './parchment.ts';
import { stripes } from './patterns.ts';
import { HILL_SHADE, FLOOD_TINT, FOREST_TINT, ICON, MARSH_TINT, MOUNTAIN_TINT, PASTEL } from './strategicPalette.ts';
import { capitalRing, city, hills, marsh, mountains, woods } from './strategicIcons.ts';
import { approach, ease } from './tween.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** After the hex edges (0), before lakes (100), rivers (110), the lens (250) and borders (300). */
export const STRATEGIC_ORDER = 90;
const STRATEGIC_FADE_S = 0.3;
const AMOUNT_EPSILON = 0.001;
/** City mark levels in the overlay texture, as texel fractions (cityMarks.ts). */
const CITY_MIN = 0.25;
const CAPITAL_MIN = 0.75;

/** Hex outline: ink at this opacity, fading once a hex spans few pixels. */
const OUTLINE_PX = 1;
const OUTLINE_INK = 0.15;
const OUTLINE_HEX_PX = [6, 16] as const;
/** Icons fade in between these hex widths (pixels). */
const ICON_HEX_PX = [7, 16] as const;
const ICON_INK = 0.9;

/**
 * Occupation and annexation (controller is not the owner): stripes in the controller's colour,
 * weighted by the share of the hex it holds. Claims (the leader's view against the default): the
 * other diagonal in the owner's colour. Both are inked toward the parchment so they read on pastel,
 * and both live in the ground colour so the flat view keeps them (borders.ts stripes only reach
 * the albedo, which the flat view mostly replaces).
 */
const OCCUPY_FREQ = 3;
const OCCUPY_DUTY = 0.42;
const OCCUPY_STRENGTH = 0.8;
const CLAIM_FREQ = 5;
const CLAIM_DUTY = 0.3;
const CLAIM_STRENGTH = 0.7;
const STRIPE_INK = 0.25;
const BYTE = 255;

/** Coast: the ink line where the ground meets sea level. */
const COAST_Y = 0.022;
const COAST_PX = 1.1;
const COAST_INK = 0.8;

/**
 * Share of the ground colour that is emissive, so light and shadow (a prop's shadow, a mountain's
 * slope) vary the flat pastel only by the rest. FLAT_LIGHT is what flat ground receives (tsl/color.ts).
 */
const FLAT_SHARE = 0.6;
const FLAT_LIGHT = 0.97;
const CAPITAL_SCALE = 1.35;
const EPS = 1e-6;

/** What a layer above the ground needs to know of it (the fog). Valid inside the same material build. */
export interface GroundTerms {
  /** The ground colour before rivers, lens and borders, so a layer can tell what those added. */
  flatBase(base: V3): V3;
  /** The emissive share of the ground colour, for a layer that covers the ground. */
  lift(): V3;
}

export interface StrategicLayer extends GroundTerms {
  layer: SurfaceLayer;
  /** 0..1, eased: how far the strategic look has faded in. */
  amount: F;
  /** Value of `amount` right now. */
  current(): number;
  set(mode: ViewMode): void;
  update(dt: number): void;
}

function flag(condition: Node<'bool'>): F {
  return select(condition, float(1), float(0));
}

/** Pastel of a land class, with its hill, feature and glacier tints. */
function ground(terrain: F, feature: F, hill: F, ice: F): V3 {
  let c: V3 = screen(PASTEL.grass);
  c = select(terrain.equal(TERRAIN.PLAINS), screen(PASTEL.plains), c);
  c = select(terrain.equal(TERRAIN.DESERT), screen(PASTEL.desert), c);
  c = select(terrain.equal(TERRAIN.TUNDRA), screen(PASTEL.tundra), c);
  c = select(terrain.equal(TERRAIN.SNOW), screen(PASTEL.snow), c);

  c = mix(c, screen(PASTEL.forest), flag(feature.equal(FEATURE.WOODS)).mul(FOREST_TINT));
  c = mix(c, screen(PASTEL.rainforest), flag(feature.equal(FEATURE.RAINFOREST)).mul(FOREST_TINT));
  c = mix(c, screen(PASTEL.marsh), flag(feature.equal(FEATURE.MARSH)).mul(MARSH_TINT));
  c = mix(c, screen(PASTEL.floodplain), flag(feature.equal(FEATURE.FLOODPLAINS)).mul(FLOOD_TINT));
  c = c.mul(mix(float(1), float(HILL_SHADE), flag(hill.equal(HILL.HILLS))));
  c = mix(c, screen(PASTEL.mountain), flag(hill.equal(HILL.MOUNTAINS)).mul(MOUNTAIN_TINT));

  return mix(c, screen(PASTEL.snow), ice.clamp(0, 1));
}

export function createStrategic(data: OverlayData, lensAmount: F): StrategicLayer {
  const amountU = uniform(0);
  let target = 0;
  let raw = 0;
  let flat: V3 | null = null;
  let lift: V3 | null = null;

  /**
   * The strategic ground over `ctx.color`. Same two TSL rules as lens.ts: inputs pinned to variables
   * before the branch, and the branch fills separate result variables.
   */
  function paint(ctx: SurfaceCtx): V3 {
    const painted = vec3(0).toVar('stratPainted');
    const under = ctx.color.toVar('stratUnder');
    const base = ctx.base.toVar('stratBase');

    const texel = ctx.data.terrainAt(ctx.hex);
    const terrain = texel.terrain.toVar('stratTerrain');
    const feature = texel.feature.toVar('stratFeature');
    const hill = texel.hill.toVar('stratHill');
    const ice = ctx.data.reliefAt(ctx.hex).z.toVar('stratIce');
    const h = ctx.world.y.toVar('stratH');
    const dh = fwidth(h).toVar('stratDh');
    const active = amountU.greaterThan(AMOUNT_EPSILON);

    If(active, () => {
      const solid = flag(terrain.greaterThan(TERRAIN.LAKE + 0.5));
      const mark = textureLoad(data.texture, ctx.hex.coord).w;
      const hexPx = float(2 * INRADIUS).div(ctx.px.max(EPS));
      const iconFade = smoothstep(ICON_HEX_PX[0], ICON_HEX_PX[1], hexPx).mul(solid);

      // Class colour; water hexes take the sea or lake pastel (the sea veil covers the sea).
      const water = select(terrain.equal(TERRAIN.LAKE), screen(PASTEL.lake), screen(PASTEL.seaShallow));
      const land = ground(terrain, feature, hill, ice);
      const flatColour = select(solid.greaterThan(0.5), land, water);

      // One icon per hex: a city over anything, then peaks, woods, hills, marsh.
      const isCity = flag(mark.greaterThan(CITY_MIN));
      const isCapital = flag(mark.greaterThan(CAPITAL_MIN));
      const isPeak = float(1).sub(isCity).mul(flag(hill.equal(HILL.MOUNTAINS)));
      const isWood = float(1).sub(isCity).sub(isPeak).mul(flag(feature.equal(FEATURE.WOODS).or(feature.equal(FEATURE.RAINFOREST))));
      const isHill = float(1).sub(isCity).sub(isPeak).sub(isWood).mul(flag(hill.equal(HILL.HILLS)));
      const isMarsh = float(1).sub(isCity).sub(isPeak).sub(isWood).sub(isHill).mul(flag(feature.equal(FEATURE.MARSH)));

      const local = ctx.hex.local;
      const peak = mountains(local, ctx.px);
      const mound = hills(local, ctx.px);
      const trees = woods(local, ctx.px);
      const reeds = marsh(local, ctx.px);
      const scale = float(1).add(isCapital.mul(CAPITAL_SCALE - 1));
      const house = city(local, ctx.px, scale);

      // Occupation and claim stripes under the icons.
      const owner = ctx.data.ownerAt(ctx.hex);
      const controller = ctx.data.controllerAt(ctx.hex);
      const held = flag(owner.greaterThan(0.5).and(controller.greaterThan(0.5)).and(controller.notEqual(owner)));
      const occupied = held.mul(ctx.data.stateAt(ctx.hex).y.div(BYTE)).mul(solid);
      const claimed = flag(owner.greaterThan(0.5).and(textureLoad(data.texture, ctx.hex.coord.add(ivec2(0, CLAIM_ROW))).x.greaterThan(0.5))).mul(solid);
      const occupyInk = mix(screenNode(ctx.data.actorColor(controller, 0)), rgb(INK), STRIPE_INK);
      const claimInk = mix(screenNode(ctx.data.actorColor(owner, 0)), rgb(INK), STRIPE_INK);
      const occupy = stripes(ctx.xz, OCCUPY_FREQ, OCCUPY_DUTY, ctx.px);
      const claim = stripes(vec2(ctx.xz.x, ctx.xz.y.negate()), CLAIM_FREQ, CLAIM_DUTY, ctx.px);

      let c = mix(flatColour, occupyInk, occupy.mul(occupied).mul(OCCUPY_STRENGTH));
      c = mix(c, claimInk, claim.mul(claimed).mul(CLAIM_STRENGTH));
      c = mix(c, screen(ICON.peak), peak.fill.mul(isPeak).mul(iconFade));
      c = mix(c, screen(ICON.peakShade), peak.shade.mul(isPeak).mul(iconFade));
      c = mix(c, screen(ICON.mound), mound.fill.mul(isHill).mul(iconFade));
      c = mix(c, screen(ICON.crown), trees.fill.mul(isWood).mul(iconFade));
      c = mix(c, screen(ICON.house), house.fill.mul(isCity).mul(iconFade));

      const iconInk = peak.stroke.mul(isPeak)
        .max(mound.stroke.mul(isHill))
        .max(trees.stroke.mul(isWood))
        .max(reeds.stroke.mul(isMarsh))
        .max(house.stroke.mul(isCity))
        .max(capitalRing(local, ctx.px, scale).mul(isCapital))
        .mul(iconFade).mul(ICON_INK);

      // Hex outline and the coast line.
      const outline = smoothstep(ctx.px.mul(OUTLINE_PX), float(0), ctx.hex.minEdge).mul(smoothstep(OUTLINE_HEX_PX[0], OUTLINE_HEX_PX[1], hexPx)).mul(OUTLINE_INK).mul(solid);
      const coast = smoothstep(COAST_PX + 1, COAST_PX, h.sub(COAST_Y).abs().div(dh.max(EPS))).mul(COAST_INK).mul(solid);

      const ink = iconInk.max(outline).max(coast).clamp(0, 1);
      painted.assign(mix(c, rgb(INK), ink));
    });

    // The albedo keeps the unshared part; a lens replaces the albedo, so it takes its emissive share too.
    const albedo = painted.mul(1 - FLAT_SHARE);
    flat = mix(base, albedo, amountU);
    lift = painted.mul(amountU).mul(FLAT_SHARE * FLAT_LIGHT).mul(float(1).sub(lensAmount));
    return mix(under, albedo, amountU);
  }

  return {
    layer: {
      name: 'strategic',
      order: STRATEGIC_ORDER,
      color: paint,
      roughness: (ctx) => mix(ctx.roughness, float(1), amountU),
      emissive: () => lift ?? vec3(0),
    },
    amount: amountU as unknown as F,
    current: () => amountU.value,
    flatBase: (base) => flat ?? base,
    lift: () => lift ?? vec3(0),
    set(mode) {
      target = mode === 'strategic' ? 1 : 0;
    },
    update(dt) {
      if (raw === target) {
        return;
      }
      raw = approach(raw, target, dt, STRATEGIC_FADE_S);
      amountU.value = ease(raw);
    },
  };
}
